const tagsView = {
  state: {
    visitedViews: [],
    cachedViews: [],
    // 新增：缓存访问时间记录，用于LRU策略
    cacheAccessTime: {}
  },
  mutations: {
    ADD_VISITED_VIEW: (state, view) => {
      if (state.visitedViews.some(v => v.path === view.path)) return
      state.visitedViews.push(
        Object.assign({}, view, {
          title: view.meta.title || 'no-name'
        })
      )
    },

    ADD_CACHED_VIEW: (state, view) => {
      const { matched, meta, name } = view

      // 如果已经缓存，更新访问时间
      if (state.cachedViews.includes(view.name)) {
        if (view.name) {
          state.cacheAccessTime[view.name] = Date.now()
        }
        return
      }

      // 检查是否明确禁止缓存
      if (meta && meta.noCache === true) {
        return
      }

      // 默认缓存所有页面，包括没有name的页面
      if (matched && matched.length > 2) {
        // 路由层级大于2时，将父级也加入cachedViews
        for (let i = 1; i < matched.length; i++) {
          const matchedName = matched[i].name
          if (matchedName && !state.cachedViews.includes(matchedName)) {
            state.cachedViews.push(matchedName)
            state.cacheAccessTime[matchedName] = Date.now()
          }
        }
      }

      // 缓存当前页面组件（无论是否有name都尝试缓存）
      if (name) {
        state.cachedViews.push(name)
        state.cacheAccessTime[name] = Date.now()
      } else if (view.name) {
        // 如果没有name但有view.name，使用view.name
        state.cachedViews.push(view.name)
        state.cacheAccessTime[view.name] = Date.now()
      } else {
        // 如果都没有，使用路径作为缓存键
        const cacheName = view.path || view.fullPath || 'unnamed-view'
        if (!state.cachedViews.includes(cacheName)) {
          state.cachedViews.push(cacheName)
          state.cacheAccessTime[cacheName] = Date.now()
        }
      }
    },

    DEL_VISITED_VIEW: (state, view) => {
      for (const [i, v] of state.visitedViews.entries()) {
        localStorage.removeItem(view.name)
        sessionStorage.removeItem(view.name)
        sessionStorage.removeItem(view.path)
        if (v.path === view.path) {
          state.visitedViews.splice(i, 1)
          break
        }
      }
    },

    DEL_CACHED_VIEW: (state, view) => {
      const index = state.cachedViews.indexOf(view.name)
      if (index > -1) {
        state.cachedViews.splice(index, 1)
        // 清理访问时间记录
        delete state.cacheAccessTime[view.name]
      }
      if (view.matched && view.matched.length > 2) {
        // 路由层级大于2，检查并移除没有子页面缓存的上级路由
        view.matched.every(() => {
          const originLength = state.cachedViews.length
          const inuseCacheNames = state.visitedViews.reduce(
            (inuseCacheNames, { matched }) => inuseCacheNames.concat(matched.map(({ name }) => name)),
            []
          )
          state.cachedViews = state.cachedViews.filter((E) => inuseCacheNames.includes(E))
          return originLength === state.cachedViews.length
        })
      }
    },

    // 新增：智能缓存清理 - LRU策略
    SMART_CACHE_CLEANUP: (state, maxSize = 20) => {
      if (state.cachedViews.length <= maxSize) return

      // 按访问时间排序，移除最久未访问的缓存
      const sortedCache = state.cachedViews
        .map(name => ({
          name,
          accessTime: state.cacheAccessTime[name] || 0
        }))
        .sort((a, b) => b.accessTime - a.accessTime)

      // 保留最近访问的页面
      const keepCache = sortedCache.slice(0, maxSize)
      const removeCache = sortedCache.slice(maxSize)

      // 更新缓存列表
      state.cachedViews = keepCache.map(item => item.name)

      // 清理被移除页面的访问时间记录
      removeCache.forEach(item => {
        delete state.cacheAccessTime[item.name]
      })
    },

    DEL_OTHERS_VISITED_VIEWS: (state, view) => {
      for (const [i, v] of state.visitedViews.entries()) {
        if (v.path === view.path) {
          state.visitedViews = state.visitedViews.slice(i, i + 1)
          break
        }
      }
    },
    DEL_OTHERS_CACHED_VIEWS: (state, view) => {
      for (const i of state.cachedViews) {
        if (i === view.name) {
          const index = state.cachedViews.indexOf(i)
          state.cachedViews = state.cachedViews.slice(index, index + 1)
          break
        }
      }
    },

    DEL_ALL_VISITED_VIEWS: state => {
      state.visitedViews = []
    },
    DEL_ALL_CACHED_VIEWS: state => {
      state.cachedViews = []
    },

    UPDATE_VISITED_VIEW: (state, view) => {
      for (let v of state.visitedViews) {
        if (v.path === view.path) {
          v = Object.assign(v, view)
          break
        }
      }
    },
    EDIT_VISITED_VIEWS: (state, view) => {
      for (const v of state.visitedViews) {
        if (v.path === view.path) {
          const item = { ...view, title: view.meta.title }
          Object.assign(v, item)
          break
        }
      }
    }

  },
  actions: {
    addView({ dispatch }, view) {
      dispatch('addVisitedView', view)
      dispatch('addCachedView', view)
    },
    addVisitedView({ commit }, view) {
      commit('ADD_VISITED_VIEW', view)
    },
    addCachedView({ commit }, view) {
      commit('ADD_CACHED_VIEW', view)
      // 添加缓存后检查是否需要清理 - 保持20个活跃缓存
      commit('SMART_CACHE_CLEANUP', 20)
    },

    delView({ dispatch, state }, view) {
      return new Promise(resolve => {
        dispatch('delVisitedView', view)
        dispatch('delCachedView', view)
        resolve({
          visitedViews: [...state.visitedViews],
          cachedViews: [...state.cachedViews]
        })
      })
    },
    delVisitedView({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_VISITED_VIEW', view)
        resolve([...state.visitedViews])
      })
    },
    delCachedView({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_CACHED_VIEW', view)
        resolve([...state.cachedViews])
      })
    },

    delOthersViews({ dispatch, state }, view) {
      return new Promise(resolve => {
        dispatch('delOthersVisitedViews', view)
        dispatch('delOthersCachedViews', view)
        resolve({
          visitedViews: [...state.visitedViews],
          cachedViews: [...state.cachedViews]
        })
      })
    },
    delOthersVisitedViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_OTHERS_VISITED_VIEWS', view)
        resolve([...state.visitedViews])
      })
    },
    delOthersCachedViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_OTHERS_CACHED_VIEWS', view)
        resolve([...state.cachedViews])
      })
    },

    delAllViews({ dispatch, state }, view) {
      return new Promise(resolve => {
        dispatch('delAllVisitedViews', view)
        dispatch('delAllCachedViews', view)
        resolve({
          visitedViews: [...state.visitedViews],
          cachedViews: [...state.cachedViews]
        })
      })
    },
    delAllVisitedViews({ commit, state }) {
      return new Promise(resolve => {
        commit('DEL_ALL_VISITED_VIEWS')
        resolve([...state.visitedViews])
      })
    },
    delAllCachedViews({ commit, state }) {
      return new Promise(resolve => {
        commit('DEL_ALL_CACHED_VIEWS')
        resolve([...state.cachedViews])
      })
    },

    updateVisitedView({ commit }, view) {
      commit('UPDATE_VISITED_VIEW', view)
    },
    editVisitedViews({ commit, state }, view) {
      return new Promise((resolve) => {
        commit('EDIT_VISITED_VIEWS', view)
        resolve([...state.visitedViews])
      })
    }
  }
}

export default tagsView
